import 'leaflet/dist/leaflet.css';

import type { Address } from '@si/types';
import type { ControlPosition, Map as LeafletMap, Marker } from 'leaflet';
import {
  icon,
  latLng as leafletLatLng,
  map as leafletMap,
  marker as leafletMarker,
  tileLayer,
} from 'leaflet';
import { useEffect, useRef } from 'react';

const markerIcon = icon({
  iconUrl:
    'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  shadowSize: [41, 41],
});

type MapProps = {
  zoomControlPosition?: ControlPosition;
  marker?: Address;
  width?: string;
  height?: string;
  zoomLevel?: 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13 | 14 | 15 | 16 | 17 | 18;
};

export function Map({
  marker,
  width = '100%',
  height = '20rem',
  zoomLevel = 15,
  zoomControlPosition = 'topleft',
}: MapProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<LeafletMap | null>(null);
  const markerRef = useRef<Marker | null>(null);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const tiles = tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 18,
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    });

    const m = leafletMap(containerRef.current, {
      center: leafletLatLng(63.4305, 10.3951), // Trondheim
      zoom: zoomLevel,
      layers: [tiles],
    });

    m.zoomControl.setPosition(zoomControlPosition);
    mapRef.current = m;

    markerRef.current = leafletMarker(leafletLatLng(0, 0), { icon: markerIcon });

    return () => {
      m.remove();
      mapRef.current = null;
      markerRef.current = null;
    };
  }, [zoomLevel, zoomControlPosition]);

  useEffect(() => {
    const m = mapRef.current;
    if (!m) return;

    const lat = marker?.latitude;
    const lng = marker?.longitude;

    if (typeof lat === 'number' && typeof lng === 'number') {
      const coords = { lat, lng };
      m.setView(coords, zoomLevel);

      if (!markerRef.current) {
        markerRef.current = leafletMarker(leafletLatLng(lat, lng), { icon: markerIcon }).addTo(m);
      } else {
        if (!m.hasLayer(markerRef.current)) markerRef.current.addTo(m);
        markerRef.current.setLatLng(coords);
      }
    } else {
      if (markerRef.current && m.hasLayer(markerRef.current)) {
        m.removeLayer(markerRef.current);
      }
    }
  }, [marker?.latitude, marker?.longitude, zoomLevel, zoomControlPosition]);

  return (
    <div style={{ height, width }}>
      <div ref={containerRef} style={{ height: '100%', width: '100%' }} />
    </div>
  );
}
